import React, { useState, useRef } from 'react';
import { useEffectOnce } from 'react-use';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import HeaderLogo from 'components/consumer/consumer-header/header-logo';
import IcCart from 'components/consumer/consumer-header/ic-cart';
import IconMaterialsDesigned from 'components/icons/iconMaterialsDesigned';
import IconClose from 'components/icons/iconClose';
import IconDiscount from 'components/icons/iconDiscount';
import IconWarningProp65 from 'components/icons/IconWarningProp65';
import IconInfoNew from 'components/icons/IconInfoNew';
import IconTrial from 'components/icons/iconTrial';
import ProgressLoader from 'components/essentials/ProgressLoader/ProgressLoader';
import LabeledTextInput from 'components/consumer/LabeledTextInput';
import { isOptionMaterial } from 'commons/constants';
import ProductImage from 'components/consumer/cart/components/ProductImage';
import {
  EXCLUDED_OPTION_SCREEN_NAMES,
  getProductStatus,
} from 'components/consumer/cart/utils';
import IconArrowDownSimple from 'components/icons/iconArrowDownSimple';
import Emoji from 'components/essentials/Emoji';
import getRandomId from 'commons/getRandomId';
import Currency from 'components/consumer/Currency';
import { convertPolicyToArray } from 'components/consumer/PDP/PDPKeySellingPoints/PDPKeySellingPointsUI';
import CtaButton from 'components/essentials/Cta/CtaButton';
import VisuallyHidden from 'components/consumer/VisuallyHidden';
import IconReturns from 'components/icons/iconReturns';
import ValidationError from 'components/consumer/ValidationError';
import MobileOnly from 'components/consumer/MobileOnly';
import TabletAndDesktopOnly from 'components/consumer/TabletAndDesktopOnly';
import Link from 'components/consumer/Link';
import IconInStock from 'components/icons/iconInStock';
import IconMadeToOrder from 'components/icons/iconMadeToOrder';

const OrderArrowDown = () => (
  <svg width={16} height={16} viewBox="0 0 11.8 6.8">
    <path d="M5.8 6.7L0 .9.9 0l4.9 5 5-5 .9.9z" />
  </svg>
);

const OrderClose = () => (
  <svg width={24} height={24} viewBox="0 0 32 32">
    <path d="M17.4 15.8l6.8-6.7c.4-.4.4-1 0-1.4s-1-.4-1.4 0L16 14.4 9.2 7.7c-.4-.4-1-.4-1.4 0-.4.4-.4 1 0 1.4l6.8 6.7-6.8 6.7c-.4.4-.4 1 0 1.4.4.4 1 .4 1.4 0l6.8-6.7 6.8 6.7c.4.4 1 .4 1.4 0s.4-1 0-1.4l-6.8-6.7z" />
  </svg>
);

const getCouponType = (couponCode, couponsList = null) => {
  if (!couponsList) return 'Discount';
  if (!couponsList[couponCode]) return '';

  let couponLabel = '';
  switch (couponsList[couponCode].type) {
    case 'reward':
      couponLabel = 'Reward';
      break;
    case 'gift':
      couponLabel = 'Gift Card';
      break;
    default:
      couponLabel = 'Discount';
      break;
  }
  return couponLabel;
};

const getDiscountAmount = (couponCode, couponsList) =>
  couponsList[couponCode].amount_used;

const getProductDescription = prod =>
  prod?.product.upholstery_product?.layout_widgets?.find(
    widget => widget.widget_identifier === 'product_description'
  )?.fields?.description_2;

const hasDiscountAndDescription = (prod, productDesc) =>
  parseInt(prod.stock_discount, 10) > 0 && productDesc;

const hasPolicy = (policyArray, policy) => policyArray.includes(policy);

const getKSPName = (prod, nextProd, productDesc, nextProdDesc) => {
  const policyArray = convertPolicyToArray(productDesc);
  const nextProdPolicyArray = convertPolicyToArray(nextProdDesc);

  if (
    hasPolicy(policyArray, '90-day returns') ===
    hasPolicy(nextProdPolicyArray, '90-day returns')
  ) {
    return '90-day';
  }
  if (
    hasPolicy(policyArray, '14-day returns') ===
    hasPolicy(nextProdPolicyArray, '14-day returns')
  ) {
    return '14-day';
  }
  if (
    parseInt(prod.stock_discount, 10) > 0 &&
    parseInt(nextProd.stock_discount, 10) > 0
  ) {
    return 'final-sale';
  }

  return null;
};

const kspToDisplay = cart => {
  let kspName = null;

  if (cart.length === 1) {
    const prod = cart[0];
    const productDesc = getProductDescription(prod);

    if (hasDiscountAndDescription(prod, productDesc)) {
      return null;
    }

    if (!productDesc) {
      return kspName;
    }

    const policyArray = convertPolicyToArray(productDesc);

    if (hasPolicy(policyArray, '90-day returns')) {
      kspName = '90-day';
    }

    if (hasPolicy(policyArray, '14-day returns')) {
      kspName = '14-day';
    }
  } else {
    for (let i = 0; i < cart.length; i++) {
      const prod = cart[i];
      const nextProd = cart[i + 1];
      const productDesc = getProductDescription(prod);

      if (hasDiscountAndDescription(prod, productDesc)) {
        return null;
      }

      if (!productDesc) {
        return kspName;
      }

      // Breaking loop when we don't have next product.
      if (!nextProd) {
        break;
      }

      const nextProdDesc = getProductDescription(nextProd);

      if (!nextProdDesc) {
        kspName = null;
        break;
      }

      kspName = getKSPName(prod, nextProd, productDesc, nextProdDesc);
      if (kspName === null) {
        break;
      }
    }
  }
  return kspName;
};

const OrderSummaryUI = ({
  cart,
  couponCode,
  couponError,
  couponSuccess,
  successCode,
  couponEditMode,
  clearError,
  validateCouponLoading,
  applyCustomerGiftsToCart,
  toggleCouponEditMode,
  updateCouponCode,
  submitCouponCode,
  orderSummary,
  summaryFetched,
  summaryIsFetched,
  removeCouponCode,
  toggleCouponSuccess,
  couponCodeRef,
  shipping,
  selectedTab,
}) => {
  const [isSummaryHeaderOpen, setIsSummaryHeaderOpen] = useState(true);
  const [expandableId, setExpandableId] = useState('');
  const [clearanceClickedId, setClearanceClickedId] = useState(false);
  const [showProp65Warning, setShowProp65Warning] = useState(false);

  const orderSummaryProductsRef = useRef();

  useEffectOnce(() => {
    setExpandableId(getRandomId('OrderSummary__expandable-panel'));
  });

  const toggleSummaryHeader = () => {
    setIsSummaryHeaderOpen(!isSummaryHeaderOpen);
  };
  const clearanceLabelClick = (cartItem, e) => {
    e.stopPropagation();
    // https://stitchindustries.atlassian.net/browse/CON-7515
    // Hide label for non-BTC products
    if (cartItem.product.efg_flag === 1 && cartItem.ship_group_mark !== 'BTC') {
      return;
    }
    setClearanceClickedId(
      clearanceClickedId === cartItem.id ? false : cartItem.id
    );
  };

  const clearanceTextClose = e => {
    e.stopPropagation();
    setClearanceClickedId(false);
  };

  const renderCouponCodes = couponsList =>
    Object.keys(couponsList).map(coupon =>
      couponsList[coupon]?.type !== 'gift_card_rewards' ? (
        <tr className="flex w-full [&_>_*]:flex-1 [&_>_*]:px-0 [&_>_*]:py-[3px]">
          <th
            scope="row"
            className="font-normal charge-summary__label !text-red coupon-code__container-code !flex !flex-row !items-center"
          >
            <div className="code">
              {getCouponType(coupon, couponsList)}: {coupon}
            </div>
            <div className="ml-1 cursor-pointer [&_svg]:flex [&_svg]:scale-50 [&_svg]:mr-1 [&_svg]:lg:scale-75">
              <IconClose onClick={() => removeCouponCode(coupon)} />
            </div>
          </th>

          <td className="charge-summary__value !text-red !text-sm !w-1/2 !text-right">
            -${getDiscountAmount(coupon, couponsList)}
          </td>
        </tr>
      ) : null
    );

  const renderOrderSummaryBody = () => {
    if (!orderSummary) {
      return null;
    }

    const {
      cart_subtotal,
      protectionSubtotal,
      discount,
      total_shipping,
      tax_amount,
      gift_card_rewards_total,
      gift_card_rewards_used,
      grand_total,
      gift_used,
      gift_details,
      applied_coupons,
      stock_discount,
      base_discount_amount,
      promotionName,
    } = orderSummary;

    if (
      !summaryFetched &&
      gift_details &&
      gift_details[0] &&
      gift_details[0].coupon_code
    ) {
      updateCouponCode({
        target: { value: gift_details[0].coupon_code },
      });
      summaryIsFetched();
    }
    const finalCoupons = applied_coupons
      ? applied_coupons.reduce((acc, cur) => {
          acc[cur.coupon_code] = cur;
          return acc;
        }, {})
      : null;
    const hasFinalCoupons = applied_coupons
      ? applied_coupons.filter(coupon => coupon !== 'gift_card_rewards')
          .length > 0
      : false;
    let finalSubTotal = cart_subtotal;
    if (!finalCoupons || promotionName !== '') {
      finalSubTotal = cart_subtotal - base_discount_amount;
    }
    if (stock_discount > 0) {
      finalSubTotal -= stock_discount;
    }

    const clearanceArray = cart.map(
      prod => parseInt(prod.stock_discount, 10) > 0
    );
    const kspDisplay = kspToDisplay(cart);
    const hasExcludeFromSale = !!cart.find(
      prod => !!prod.options?.find(o => o.exclude_from_sale)
    );

    return (
      <div className="order-summary-body bg-transparent text-gray text-[16px] tracking-[-0.13px] leading-[18px] p-0 flex-1">
        <div className="p-0">
          <table className="block w-full">
            <tbody className="block w-full">
              <tr className="flex w-full flex-1 px-0 py-[3px]">
                <th
                  scope="row"
                  className="font-normal charge-summary__label !text-sm !text-gray !w-1/2 !text-left"
                >
                  Subtotal
                </th>

                <td
                  className="charge-summary__value !text-sm !text-gray !w-1/2 !text-right"
                  data-xc="subtotal"
                >
                  <Currency>{finalSubTotal}</Currency>
                </td>
              </tr>

              {protectionSubtotal > 0 && (
                <tr className="flex w-full flex-1 px-0 py-[3px]">
                  <th
                    scope="row"
                    className="font-normal charge-summary__label !text-sm !text-gray !w-1/2 !text-left"
                  >
                    Protection Plan
                  </th>

                  <td
                    className="charge-summary__value !text-sm !text-gray !w-1/2 !text-right"
                    data-xc="protection-subtotal"
                  >
                    <Currency>{protectionSubtotal}</Currency>
                  </td>
                </tr>
              )}

              <tr className="flex w-full flex-1 px-0 py-[3px]">
                <th
                  scope="row"
                  className="font-normal charge-summary__label !text-sm !text-gray !w-1/2 !text-left"
                >
                  Shipping
                </th>

                <td
                  className="charge-summary__value !text-sm !text-gray !w-1/2 !text-right"
                  data-xc="shipping"
                >
                  <Currency>{total_shipping}</Currency>
                </td>
              </tr>

              <tr className="flex w-full flex-1 px-0 py-[3px]">
                <th
                  scope="row"
                  className="font-normal charge-summary__label !text-sm !text-gray !w-1/2 !text-left"
                >
                  Sales Tax
                </th>

                <td
                  className="charge-summary__value !text-sm !text-gray !w-1/2 !text-right"
                  data-xc="tax"
                >
                  <Currency>{tax_amount}</Currency>
                </td>
              </tr>

              {hasFinalCoupons && renderCouponCodes(finalCoupons)}

              {gift_card_rewards_total > 0 &&
                (gift_card_rewards_used > 0 ? (
                  <tr className="flex w-full flex-1 px-0 py-[3px] credit text-[#d0021b]">
                    <th
                      scope="row"
                      className="font-normal charge-summary__label rewards-label !flex !items-center !text-sm !text-gray !w-1/2 !text-left"
                    >
                      Credits
                      <span className="ml-1 cursor-pointer [&_svg]:flex [&_svg]:scale-50 [&_svg]:mr-1 [&_svg]:lg:scale-75">
                        <IconClose
                          onClick={() => applyCustomerGiftsToCart(false)}
                        />
                      </span>
                    </th>

                    <td
                      className="charge-summary__value rewards-value text-[#d0021b] !text-sm !w-1/2 !text-right"
                      data-xc="gift-used"
                    >
                      - <Currency>{gift_card_rewards_used}</Currency>
                    </td>
                  </tr>
                ) : (
                  <tr className="flex w-full flex-1 px-0 py-[3px]">
                    <td colSpan="2" className="mt-1">
                      <CtaButton
                        hasArrow={false}
                        onClick={() => applyCustomerGiftsToCart(true)}
                        styling="link"
                        color="brand"
                        size="xs"
                        spacing="sm"
                      >
                        Apply available credit: ${gift_card_rewards_total}
                      </CtaButton>
                    </td>
                  </tr>
                ))}

              <tr className="flex w-full flex-1 px-0 py-[3px] charge-summary__table-row--order-total [&>*]:![border-top:1px_solid_#e5e5e5] [&>*]:!mt-4 [&>*]:!pt-4">
                <th
                  scope="row"
                  className="font-normal charge-summary__label !text-gray !w-1/2 !text-left !text-lg"
                >
                  Total
                </th>

                <td
                  className="charge-summary__value !text-lg !text-gray !w-1/2 !text-right !font-bold"
                  data-xc="shipping"
                >
                  <Currency>{grand_total}</Currency>
                </td>
              </tr>

              {base_discount_amount > 0 &&
                !(gift_card_rewards_used > 0 || gift_used > 0) && (
                  <tr className="flex w-full flex-1 px-0 py-[3px]">
                    <th
                      scope="row"
                      className="font-normal charge-summary__label text-red !text-sm !w-1/2 !text-left"
                    >
                      You saved
                    </th>

                    <td
                      className="charge-summary__value text-red !text-sm !w-1/2 !text-right"
                      data-xc="cart-discount"
                    >
                      <Currency>{base_discount_amount}</Currency>
                    </td>
                  </tr>
                )}

              {discount > 0 && (gift_card_rewards_used > 0 || gift_used > 0) && (
                <tr className="flex w-full flex-1 px-0 py-[3px]">
                  <th
                    scope="row"
                    className="charge-summary__label text-red text-sm font-bold pt-[5px] !w-1/2 !text-left"
                  >
                    You Saved
                  </th>

                  <td
                    className="charge-summary__value text-red text-sm font-bold pt-[5px] !w-1/2 !text-right"
                    data-xc="discount"
                  >
                    <Currency>
                      {discount + gift_card_rewards_used + gift_used}
                    </Currency>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="relative mx-0 my-8 w-full">
            <button
              className="text-base font-bold font-sans text-brand cursor-pointer [background:none] border-0"
              onClick={() => {
                setTimeout(() => {
                  if (!couponEditMode && couponCodeRef?.current) {
                    couponCodeRef.current.focus();
                  }
                }, 50);

                toggleCouponEditMode();
              }}
              type="button"
              aria-expanded={couponEditMode ? 'true' : 'false'}
            >
              Apply Gift Card/Promo Code {couponEditMode ? '-' : '+'}
            </button>

            {couponEditMode && (
              <div className="relative">
                <div className="mt-[15px]">
                  <LabeledTextInput
                    id="coupon_code"
                    name="coupon_code"
                    ref={couponCodeRef}
                    type="text"
                    label="Enter code"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    value={couponCode}
                    onChange={updateCouponCode}
                    onFocus={clearError}
                    aria-label="Gift Card/Promo Code"
                    action={
                      <div className="items-center flex justify-end">
                        {!!validateCouponLoading && (
                          <div className="mr-2">
                            <ProgressLoader
                              size={16}
                              style={{ color: '#9a9a9a' }}
                            />
                          </div>
                        )}

                        {!couponSuccess ? (
                          <button
                            className="text-sm font-bold items-center bg-transparent border-0 rounded-none text-[#9a9a9a] cursor-pointer flex text-center"
                            onClick={submitCouponCode}
                            type="button"
                            aria-label="Apply Code"
                          >
                            Apply
                          </button>
                        ) : (
                          undefined
                        )}
                      </div>
                    }
                    errorMessage={
                      couponError ? (
                        <ValidationError block>
                          {couponError === 'ValidNA' ? (
                            <>
                              <span>
                                This promo code is not valid for the items in
                                this order
                              </span>
                              {!hasExcludeFromSale && (
                                <span>
                                  <b>BUT, the good news is,</b> you&apos;re
                                  saving more on our current sale than you would
                                  be with your promo code at regular prices.{' '}
                                  <Emoji symbol="😎" inline />{' '}
                                </span>
                              )}
                            </>
                          ) : (
                            couponError
                          )}
                        </ValidationError>
                      ) : (
                        undefined
                      )
                    }
                  />
                </div>
              </div>
            )}
            {couponSuccess && (
              <div className="bg-brand-lightest min-h-[53px] w-full flex flex-row p-2 justify-between mt-4">
                <div className="text-sm flex flex-row items-center">
                  <div className="w-[30px] flex justify-end items-end mr-[.3rem] lg:justify-center lg:items-center [&_svg]:flex [&_svg]:transform-[scale(.65)] [&_svg]:mr-1 [&_svg]:lg:scale-75">
                    <IconMaterialsDesigned color="#27919C" />
                  </div>{' '}
                  {!successCode && (
                    <span>
                      Your {getCouponType(couponCode).toLowerCase()} was
                      successfully applied to this order!
                    </span>
                  )}
                  {successCode && successCode === 'REPLACED' && (
                    <div>
                      <span>
                        Your {getCouponType(couponCode).toLowerCase()} was
                        successfully applied to this order!
                      </span>
                      <span className="text-sm font-bold text-gray block pt-1">
                        The promo code you entered is either the same as, or
                        larger than our current sale discount. In either case,
                        the current sale cannot be combined with the promo code
                        you entered — we’ve removed the current sale, and
                        updated your totals to reflect the promo code discount.
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-sm flex flex-row items-center">
                  <div className="w-[30px] flex justify-end items-end mr-[.3rem] lg:justify-center lg:items-center [&_svg]:flex [&_svg]:transform-[scale(.65)] [&_svg]:mr-1 [&_svg]:lg:scale-75">
                    <IconClose onClick={() => toggleCouponSuccess()} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 md:mt-16">
            {kspDisplay === '90-day' && (
              <div className="mt-4 first:mt-0 last:max-[767px]:mb-4 md:mt-12">
                <div className="items-center flex flex-row mb-2.5">
                  <IconReturns width="29" height="28" />
                  <h3 className="text-base font-bold text-gray mt-0 mr-0 mb-[3px] ml-3">
                    90 Day Returns
                  </h3>
                </div>
                <div className="text-xs text-gray">
                  Sit on it, break it in - if you don’t love your Joybird, send
                  it back to us within 90 days of delivery, and you’ll get a
                  refund of the order’s subtotal, minus a 50% restocking fee and
                  shipping fees.
                </div>
              </div>
            )}

            {kspDisplay === '14-day' && (
              <div className="mt-4 first:mt-0 last:max-[767px]:mb-4 md:mt-12">
                <div className="items-center flex flex-row mb-2.5">
                  <IconReturns width="29" height="28" />
                  <h3 className="text-base font-bold text-gray mt-0 mr-0 mb-[3px] ml-3">
                    14 Day Returns
                  </h3>
                </div>
                <div className="text-xs text-gray">
                  Please contact us within 14 days of receiving your order for
                  product-specific return procedures. Once we receive your
                  return, you’ll get a full refund - minus a 25% restocking fee.
                  Return shipment costs are the responsibility of the customer.
                </div>
              </div>
            )}

            {!clearanceArray.every(value => value === true) && (
              <div className="mt-4 first:mt-0 last:max-[767px]:mb-4 md:mt-12">
                <div className="items-center flex flex-row mb-2.5">
                  <IconTrial width="25" height="18" />
                  <h3 className="text-base font-bold text-gray mt-0 mr-0 mb-[3px] ml-3">
                    Changed your mind?
                  </h3>
                </div>
                <div className="text-xs text-gray">
                  If you need to cancel your order, you must cancel within 24
                  hours in order to receive a full refund. Made to Order pieces
                  cannot be cancelled after 24 hours and are not eligible for
                  returns. For full details on returns & cancellations please
                  visit: <Link to="/returns/">joybird.com/returns</Link>
                </div>
              </div>
            )}

            {shipping?.state === 'CA' && selectedTab === 'delivery' && (
              <div className="mt-4 first:mt-0 last:max-[767px]:mb-4 md:mt-12">
                <div className="items-center flex flex-row mb-2.5">
                  <IconWarningProp65 width="24" height="24" />
                  <button
                    type="button"
                    className="text-base font-bold font-sans text-brand no-underline focus:outline-0 focus-visible:underline hover:underline mt-0 mr-0 mb-[3px] ml-3 bg-transparent border-0 cursor-pointer p-0"
                    onClick={() => setShowProp65Warning(!showProp65Warning)}
                  >
                    WARNING CA Prop 65
                  </button>
                </div>
                {showProp65Warning && (
                  <div className="w-full p-4 mt-2.5 relative bg-[#F6F6F6] md:bg-white text-gray text-sm">
                    <div className="flex flex-row items-center mb-1">
                      <span className="flex items-center justify-center mr-1">
                        <IconWarningProp65 width="16" height="16" />
                      </span>
                      <span className="font-bold">WARNING:</span>
                    </div>
                    <div>
                      This product can expose you to chemicals including vinyl
                      acetate, which is known to the State of California to
                      cause cancer or birth defects or other reproductive harm.
                      For more information go to{' '}
                      <a
                        href="https://www.P65Warnings.ca.gov"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray underline hover:text-gray-dark"
                      >
                        www.P65Warnings.ca.gov
                      </a>
                      .
                    </div>
                  </div>
                )}
              </div>
            )}

            {kspDisplay === 'final-sale' && (
              <div className="mt-4 first:mt-0 last:max-[767px]:mb-4 md:mt-12">
                <div className="items-center flex flex-row mb-2.5">
                  <IconTrial width="25" height="25" />
                  <h3 className="text-base font-bold text-gray mt-0 mr-0 mb-[3px] ml-3">
                    Final Sale
                  </h3>
                </div>
                <div className="text-xs text-gray space-y-2">
                  <p>
                    {cart.length === 1
                      ? 'This product is'
                      : 'These products are'}{' '}
                    final sale and cannot be returned or exchanged.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderOrderSummaryHeader = () => {
    const cartItemsCount = cart
      ? cart.reduce((sum, p) => (p.quantity || 0) + sum, 0)
      : 0;

    return (
      <>
        <MobileOnly>
          {/* Note: this element should ideally be a button to provide full
      accessibility but in this case we can't do this because there are
      button elements nested within it */}
          <div
            className="md:hidden flex relative pb-4 justify-between [&_h4]:pt-1"
            onClick={toggleSummaryHeader}
            role="button"
            tabIndex="0"
          >
            {!isSummaryHeaderOpen ? (
              <div className="pt-1">
                <HeaderLogo />
              </div>
            ) : (
              <h4>Your cart</h4>
            )}
            <div className="order-summary-header__cart flex">
              {!isSummaryHeaderOpen && (
                <>
                  <IcCart className="!-top-2" />
                  {orderSummary && (
                    <Currency className="mt-0 mr-9 mb-0 ml-1">
                      {orderSummary.grand_total}
                    </Currency>
                  )}
                </>
              )}
            </div>
            <button
              className="absolute right-0 top-1 border-0 w-6 h-6 bg-transparent"
              type="button"
            >
              {!isSummaryHeaderOpen ? <OrderArrowDown /> : <OrderClose />}
            </button>
          </div>
        </MobileOnly>

        <TabletAndDesktopOnly>
          <div className="max-[767px]:hidden items-center flex flex-row justify-between w-full [&_>_*]:flex-1">
            <h2
              className={classNames(
                'order-summary-header__title text-base font-bold m-0',
                { hidden: isSummaryHeaderOpen }
              )}
            >
              Your Cart
            </h2>
            <div
              className={classNames(
                'order-summary-header__product-count font-bold pr-4',
                {
                  'text-sm text-right': !isSummaryHeaderOpen,
                  'text-base flex-grow text-left': isSummaryHeaderOpen,
                }
              )}
            >
              {cartItemsCount} item
              {cartItemsCount !== 1 ? 's' : ''}
            </div>
            <span
              className={classNames(
                'order-summary-header__toggle flex !flex-grow-0 [&_svg]:[transition:all_0.25s_ease-in-out]',
                {
                  '[&_svg]:!rotate-180 origin-center': isSummaryHeaderOpen,
                }
              )}
            >
              <IconArrowDownSimple />
            </span>
          </div>
        </TabletAndDesktopOnly>

        <div
          className={classNames(
            'order-summary-header__content w-full flex overflow-hidden flex-col max-[767px]:min-[560px]:flex-row [&_.cart-item-photo]:mr-5',
            {
              'h-px [transition:height_0.2s_ease-in]': !isSummaryHeaderOpen,
              'h-auto [transition:height_0.2s_ease-out]': isSummaryHeaderOpen,
            }
          )}
          ref={orderSummaryProductsRef}
          aria-hidden={!isSummaryHeaderOpen ? 'true' : undefined}
        >
          <div className="flex-1">
            {cartItemsCount > 0 &&
              cart.map(prod => {
                const productionStatus = getProductStatus(prod);
                const optionsNames = prod.options
                  ? prod.options
                      .filter(option => option.optionTypeId !== 19)
                      .map(o =>
                        prod.pickLater && isOptionMaterial[o.optionTypeId]
                          ? 'Material: Decide Later'
                          : EXCLUDED_OPTION_SCREEN_NAMES.includes(
                              o.optionTypeId
                            )
                          ? o.optionTypeId === 133 && o.name === 'Included'
                            ? 'Extra Cushion'
                            : ''
                          : `${o.name} ${o.optionTypeName} ${
                              o.exclude_from_sale ? ' - Limited Edition' : ''
                            }`
                      )
                  : [];
                return (
                  <div key={prod.id + prod.name}>
                    <div
                      className="order-summary-header__content-row flex mt-5 [&_.cart-item-photo]:w-[100px]"
                      key={prod.id + prod.name}
                    >
                      <ProductImage
                        product={prod}
                        height={80}
                        containerClass="mr-6 rounded flex justify-center items-center border border-solid border-gray-light8 bg-gray-light7 w-24 sm:w-28 md:p-2 after:content-[''] after:block after:pb-[100%] cursor-pointer h-fit md:w-40"
                      />

                      <div className="w-full h-auto flex">
                        <div className="w-2/3 h-auto">
                          <h3 className="order-summary-header__content-name text-sm font-bold leading-[1.75] text-gray">
                            {prod.name}
                          </h3>

                          <div className="text-sm leading-[1.75] text-gray font-normal">
                            <div>
                              {optionsNames.filter(v => !!v).join(', ')}
                            </div>
                            <div>Qty {prod.quantity}</div>
                          </div>
                        </div>

                        <div className="w-2/3 h-auto flex-1 justify-end">
                          <div className="text-sm text-right">
                            {prod.discount_amount > 0 ? (
                              orderSummary?.applied_coupons?.length > 0 &&
                              orderSummary.promotionName === '' ? (
                                <div>${prod.price_pre_discount}</div>
                              ) : (
                                <div className="flex justify-end items-center">
                                  <VisuallyHidden>
                                    Original price:
                                  </VisuallyHidden>
                                  <div className="text-gray pr-2 line-through">
                                    ${prod.price_pre_discount}
                                  </div>
                                  <VisuallyHidden>Price:</VisuallyHidden>
                                  <div className="text-red">${prod.price}</div>
                                </div>
                              )
                            ) : (
                              <div className="price">${prod.price}</div>
                            )}
                            {productionStatus && (
                              <button
                                type="button"
                                onClick={e => {
                                  clearanceLabelClick(prod, e);
                                }}
                                className="inline-flex px-2 bg-yellow-light rounded-sm text-[0.66rem] mt-1 items-center whitespace-nowrap"
                              >
                                {productionStatus.bannerText}
                                {productionStatus.type === 'ready_to_ship' && (
                                  <IconInStock
                                    width={15}
                                    height={15}
                                    className="ml-1"
                                  />
                                )}
                                {productionStatus.type === 'made_to_order' && (
                                  <IconMadeToOrder
                                    width={11}
                                    height={11}
                                    className="ml-1"
                                  />
                                )}
                                {productionStatus.type === 'final_sale' && (
                                  <IconInfoNew
                                    width={13}
                                    height={13}
                                    className="ml-1"
                                  />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    {clearanceClickedId === prod.id && (
                      <div className="w-full bg-yellow-light text-gray my-4 font-normal flex justify-center items-start rounded-sm">
                        <div className="py-4 px-2">
                          {productionStatus.type === 'stocked_item' && (
                            <IconInStock height={24} className="mr-2.5" />
                          )}
                          {productionStatus.type === 'made_to_order' && (
                            <IconMadeToOrder height={24} className="mr-2.5" />
                          )}
                          {productionStatus.type === 'clearance' && (
                            <IconDiscount className="mr-2.5" />
                          )}
                        </div>
                        <div className="py-2">
                          <p className="text-base font-bold">
                            {productionStatus.bannerText}
                          </p>
                          <p className="text-sm whitespace-pre-line">
                            {productionStatus.contentText}
                          </p>
                        </div>
                        <button
                          onClick={clearanceTextClose}
                          type="button"
                          aria-label="close"
                          className="p-4"
                        >
                          <IconClose color="#424242" height={10} width={10} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          <MobileOnly>
            <div className="md:hidden">{renderOrderSummaryBody()}</div>
          </MobileOnly>
        </div>
      </>
    );
  };

  const headerClass = classNames(
    'order-summary-header p-0 text-[20px] font-bold bg-transparent tracking-[-0.28px] leading-[24px] text-gray flex flex-col text-left border-0 md:items-center md:bg-white md:rounded md:shadow-[0_0.0625rem_0_rgba(0,0,0,0.05)_,0_0.0625rem_0.125rem_rgba(0,0,5,0.1)_,0_0.3125rem_0.9375rem_rgba(0,0,0,0.05)] md:cursor-pointer md:mb-5 md:outline-0 md:px-6 md:py-4 md:[transition:box-shadow_0.15s_ease-out_0s] md:w-full hover:md:shadow-[0_0.3125rem_0.9375rem_rgba(0,0,0,0.05)_,0_0.3125rem_0.3125rem_rgba(0,0,0,0.1)_,0_0.125rem_0.3125rem_rgba(0,0,0,0.05)]',
    {
      'order-summary-header--open': isSummaryHeaderOpen,
    }
  );

  return (
    <div className="order-summary basis-full min-w-0" data-xc="order-summary">
      <MobileOnly>
        <div className={classNames(headerClass, 'md:hidden')}>
          {renderOrderSummaryHeader()}
        </div>
      </MobileOnly>

      <TabletAndDesktopOnly>
        {/* Note: this element should ideally be a button to provide full
        accessibility but in this case we can't do this because there are
        button elements nested within it */}
        <div
          role="button"
          tabIndex="0"
          aria-label="Show/hide order summary"
          className={classNames(headerClass, 'max-[767px]:hidden')}
          onClick={toggleSummaryHeader}
          aria-expanded={!!isSummaryHeaderOpen}
          aria-controls={expandableId}
        >
          {renderOrderSummaryHeader()}
        </div>
      </TabletAndDesktopOnly>

      <TabletAndDesktopOnly>
        <div className="max-[767px]:hidden">{renderOrderSummaryBody()}</div>
      </TabletAndDesktopOnly>
    </div>
  );
};

OrderSummaryUI.propTypes = {
  applyCustomerGiftsToCart: PropTypes.func,
  cart: PropTypes.object,
  clearError: PropTypes.func,
  couponCode: PropTypes.string,
  couponSuccess: PropTypes.bool,
  couponCodeRef: PropTypes.object,
  couponEditMode: PropTypes.bool,
  couponError: PropTypes.string,
  orderSummary: PropTypes.shape({
    applied_coupons: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string,
        discount: PropTypes.shape({
          amount: PropTypes.number,
          currency: PropTypes.string,
        }),
      })
    ),
    base_discount_amount: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    cart_subtotal: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    discount: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    grand_total: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    gift_card_rewards_total: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    gift_card_rewards_used: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    gift_details: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.shape({
          amount: PropTypes.number,
          currency: PropTypes.string,
        }),
        code: PropTypes.string,
        coupon_code: PropTypes.string,
        description: PropTypes.string,
      })
    ),
    gift_used: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    grand_total_after_discount: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    grand_total_before_discount: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    promotionName: PropTypes.string,
    protectionSubtotal: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    shipping_total: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    stock_discount: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    subtotal: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    tax_amount: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    tax_total: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    total_shipping: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
  }),
  removeCouponCode: PropTypes.func,
  submitCouponCode: PropTypes.func,
  successCode: PropTypes.string,
  summaryFetched: PropTypes.bool,
  summaryIsFetched: PropTypes.func,
  toggleCouponEditMode: PropTypes.func,
  toggleCouponSuccess: PropTypes.func,
  updateCouponCode: PropTypes.func,
  validateCouponLoading: PropTypes.bool,
};

export default OrderSummaryUI;
